import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';

interface Ctx {
  isMobile: boolean;
  open: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
}
const CtxObj = createContext<Ctx | null>(null);
export const MobileSearchProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const mq = useMemo(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 1023px)') : null),
    [],
  );
  const [isMobile, setIsMobile] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [open, setOpen] = useState(false);

  // keep isMobile in sync
  useEffect(() => {
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mq]);

  useEffect(() => {
    const handleHashChange = () => {
      setOpen(window.location.hash === '#filter-bar__menu');
    };

    // Run on initial load in case the page is loaded with the hash already present
    handleHashChange();

    // Listen for any subsequent hash changes (e.g., clicking the links)
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup: remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const { body } = document;
    if (open) {
      body.classList.add('mobile-search-open');
      body.style.overflow = 'hidden';
      body.style.height = '100%';
    } else {
      body.classList.remove('mobile-search-open');
      body.style.overflow = '';
      body.style.height = '';
    }
    return () => {
      body.classList.remove('mobile-search-open');
      body.style.overflow = '';
      body.style.height = '';
    };
  }, [open, isMobile]);

  const value: Ctx = {
    isMobile,
    open,
    openSearch: () => setOpen(true),
    closeSearch: () => setOpen(false),
    toggleSearch: () => setOpen((v) => !v),
  };

  return <CtxObj.Provider value={value}>{children}</CtxObj.Provider>;
};

export const useMobileSearch = () => {
  const ctx = useContext(CtxObj);
  if (!ctx) throw new Error('useMobileSearch must be used within MobileSearchProvider');
  return ctx;
};
